import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../../site/auth";
import { DEPOSIT, FREE_KRW } from "../data";
import { LaonaMark } from "../LaonaApp";
import SelfProtoMark from "../SelfProtoMark";
import { manualApi, projectApi } from "./LaonaProjects";

/* =========================================================
   시제품을 만드는 두 가지 방법 — 소개(/laonabot) 맨 위와 프로젝트 시작(/laonabot/start)에서 같이 쓴다.
   카드에는 제목 · 가격 · 짧은 설명만 두고, 자세한 내용(할 수 있는 것 · 받는 것 · 비용)은 [자세히 보기]로 펼친다 (2026-11).
   카드 어디를 눌러도([자세히 보기]와 펼친 내용은 빼고) 프로젝트 만들기 팝업(이름 → 시작하기)이 뜬다. 팝업은 주소의 ?way=manual|bot 로 열려서
   로그인 · 이메일 인증을 거치고 돌아와도 이어서 열리고, 뒤로 가기로 닫힌다.
   ========================================================= */

export const WAYS = [
  {
    id: "manual",
    name: "직접 만들기",
    price: "무료",
    desc: "모듈을 직접 배치하고 배선해 나만의 장치를 만듭니다.\n펌웨어 개발의뢰는 옵션으로 선택할 수 있습니다.",
    who: "회원이면 누구나 무료로 쓸 수 있습니다.",
    items: [
      "연결도 도구(PinWire)에서 모듈을 끌어다 놓고 직접 배선",
      "브레드보드 배치 · 점퍼선 배선까지 자동 계산",
      "내 프로젝트에서 필요한 부품을 한 번에 주문",
      "펌웨어가 필요하면 장바구니에 담을 때 [펌웨어 개발 옵션]을 선택 (기본 5만원, 기능정의서 작성 후)",
    ],
    get: "결선도, 배선도, BOM, 부품(모듈, 브레드보드, 점퍼케이블)",
    note: "최종 결산 = 부품값 + 배송비 (+ 펌웨어 개발 옵션을 고르면 기본 5만원)",
  },
  {
    id: "bot",
    name: "라오나봇과 만들기",
    price: "무료로 시작",
    desc: "대화만으로 설계부터 펌웨어까지 라오나봇이 진행합니다.",
    who: "만들고 싶은 것을 말하면 기능 정리 · 모듈 선정 · 배선 설계를 라오나봇이 맡습니다.",
    items: [
      "라오나봇과 대화로 기능 정리 · 모듈 선정 · 배선 자동 설계",
      `사용량이 ${FREE_KRW.toLocaleString("ko-KR")}원을 넘으면 이용료 ${DEPOSIT.toLocaleString("ko-KR")}원으로 이어서 대화 · 설계`,
      "설계를 마치면 내 프로젝트로 저장하고, 부품과 펌웨어 개발 · 빌드(기본 5만원)를 함께 주문",
      "펌웨어는 바이너리를 포함해 코드 전체를 제공, 부품은 키트로 배송",
    ],
    get: "결선도, 배선도, BOM, 부품(모듈, 브레드보드, 점퍼케이블), 펌웨어 코드(바이너리 포함)",
    note: "주문 = 부품값 + 배송비 + 펌웨어 개발 · 빌드 기본 5만원 (보통 추가 비용 없음)",
  },
];

/* AI 설계 서비스가 열려 있는지 (관리자는 멈춘 동안에도 열림). 불러오기 전에는 null */
export function useLaonaService() {
  const { member } = useAuth();
  const [svc, setSvc] = useState(null);
  useEffect(() => {
    let alive = true;
    fetch("/api/laona/projects/service", { credentials: "same-origin" })
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((d) => alive && setSvc(d))
      .catch(() => alive && setSvc({ ai: true, enabled: true, admin: false }));
    return () => { alive = false; };
  }, [member]);
  return svc;
}

const HOW_ID = "with-laonabot";

/* 카드 오른쪽 그림 — 제목 · 설명 옆에 띄운다. 좁은 카드에서는 감춘다 (laona.css .lb-way__art)
   배경을 투명하게 뺀 그림이라 밝은 · 어두운 화면 모두에 쓴다 (public/laonabot/way-*.webp) */
const WAY_ART = { manual: "/laonabot/way-manual.webp", bot: "/laonabot/way-bot.webp" };

function WayArt({ id }) {
  return <img className="lb-way__art" src={WAY_ART[id]} alt="" width="640" height="470" loading="lazy" decoding="async" />;
}

export function WayCards({ className = "" }) {
  const { member } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const svc = useLaonaService();
  const aiOpen = svc ? svc.ai : true;

  const [open, setOpen] = useState({}); // [자세히 보기]로 펼친 카드
  const way = params.get("way");
  const dialogWay =
    member?.email_verified && (way === "manual" || (way === "bot" && svc?.ai)) ? way : null;

  function pick(id) {
    const target = `/laonabot/start?way=${id}`;
    if (!member) navigate("/laonabot/signup", { state: { from: target } });
    else if (!member.email_verified) navigate(target);
    else setParams((p) => { p.set("way", id); return p; });
  }
  function closeDialog() {
    setParams((p) => { p.delete("way"); return p; }, { replace: true });
  }
  /* 소개 페이지에서는 아래 설명으로 내려가고, 다른 페이지에서는 소개의 설명으로 간다 */
  function toHow(e) {
    e.stopPropagation();
    e.preventDefault();
    const el = document.getElementById(HOW_ID);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    else navigate(`/laonabot#${HOW_ID}`);
  }

  return (
    <>
      <div className={`lb-ways lb-ways--pick ${className}`}>
        {WAYS.map((w) => {
          const off = w.id === "bot" && !aiOpen;
          const pickProps = off ? {} : {
            role: "button",
            tabIndex: 0,
            "aria-labelledby": `way-${w.id}-name`,
            "aria-describedby": `way-${w.id}-who`,
            onClick: () => pick(w.id),
            onKeyDown: (e) => {
              if (e.target !== e.currentTarget) return;
              if (e.key === "Enter" || e.key === " ") { e.preventDefault(); pick(w.id); }
            },
          };
          return (
            <article key={w.id} className={`lb-way ${off ? "lb-way--off" : "lb-way--pick"}`} {...pickProps}>
              <div className="lb-way__head">
                <h2 className="lb-way__title" id={`way-${w.id}-name`}>
                  {w.id === "bot" ? <LaonaMark size={30} /> : <SelfProtoMark size={30} ink="var(--lb-accent-ink)" className="lb-mark" />}
                  {w.name}
                </h2>
                <b className="lb-way__price">{off ? "준비 중" : w.price}</b>
              </div>
              <div className="lb-way__body">
                <p className="lb-way__desc" id={`way-${w.id}-who`}>{w.desc}</p>
                <WayArt id={w.id} />
              </div>
              {off && (
                <p className="lb-way__off">
                  라오나봇 AI 설계 서비스는 지금 준비 중입니다. 열리면 이곳에서 바로 시작할 수 있습니다.
                  그동안 '직접 만들기'로 연결도를 그리고 부품을 주문할 수 있습니다.
                </p>
              )}
              {/* 자세한 내용 — 카드를 눌러 시작하는 것과 겹치지 않게 버튼 · 펼친 내용의 클릭은 카드로 올리지 않는다 */}
              <div className={`lb-way__more ${open[w.id] ? "is-open" : ""}`} id={`way-${w.id}-more`}
                   onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
                <div className="lb-way__more-in" inert={!open[w.id]}>
                  <p className="lb-way__who">{w.who}</p>
                  <ul className="lb-way__list">
                    {w.items.map((t) => <li key={t}>{t}</li>)}
                  </ul>
                  <p className="lb-way__get"><span>받는 것</span>{w.get}</p>
                  {w.note && !off && <p className="lb-way__note">{w.note}</p>}
                  {!off && <p className="lb-way__note">주문은 내 프로젝트에서 할 수 있습니다.</p>}
                  {w.id === "bot" && (
                    <a href={`/laonabot#${HOW_ID}`} className="lb-way__how" onClick={toHow}>
                      라오나봇과 만드는 방법 보기 <span aria-hidden="true">↓</span>
                    </a>
                  )}
                </div>
              </div>
              <button type="button" className="lb-way__toggle" aria-expanded={!!open[w.id]} aria-controls={`way-${w.id}-more`}
                      onClick={(e) => { e.stopPropagation(); setOpen((o) => ({ ...o, [w.id]: !o[w.id] })); }}
                      onKeyDown={(e) => e.stopPropagation()}>
                {open[w.id] ? "간단히 보기" : "자세히 보기"}
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4"
                     strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false"><path d="m6 9 6 6 6-6" /></svg>
              </button>
            </article>
          );
        })}
      </div>
      {dialogWay && <NewProjectDialog key={dialogWay} way={dialogWay} svc={svc} onClose={closeDialog} />}
    </>
  );
}

/* 프로젝트 만들기 팝업 — 이름을 받고 시작하기 (라오나봇도 무료로 시작한다) */
function NewProjectDialog({ way, svc, onClose }) {
  const navigate = useNavigate();
  const ref = useRef(null);
  const [title, setTitle] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const bot = way === "bot";

  useEffect(() => {
    const d = ref.current;
    if (d && !d.open) d.showModal();
    d?.querySelector("#lbNewTitle")?.focus();
  }, []);

  async function submit(e) {
    e.preventDefault();
    if (!title.trim()) { setError(`프로젝트 이름을 적어 주세요. 예: ${bot ? "화분 자동 물주기" : "온습도 표시기"}`); return; }
    setBusy(true);
    setError("");
    try {
      if (bot) {
        const p = await projectApi("", { method: "POST", body: JSON.stringify({ title: title.trim() }) });
        navigate(`/laonabot/projects/${p.id}`);
      } else {
        const p = await manualApi("", { method: "POST", body: JSON.stringify({ title: title.trim() }) });
        navigate(`/laonabot/wiring?manual=${p.id}`);
      }
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  }

  return (
    <dialog
      ref={ref}
      className="lb-dialog"
      aria-labelledby="lb-dialog-title"
      onClose={onClose}
      onClick={(e) => { if (e.target === ref.current && !busy) ref.current.close(); }}
    >
      <form className="lb-dialog__box" onSubmit={submit}>
        <div className="lb-dialog__head">
          <h2 id="lb-dialog-title">
            {bot ? <LaonaMark size={24} /> : <SelfProtoMark size={24} ink="var(--lb-accent-ink)" className="lb-mark" />}
            {bot ? "라오나봇과 만들기" : "직접 만들기"}
          </h2>
          <button type="button" className="lb-dialog__x" aria-label="닫기" disabled={busy} onClick={() => ref.current.close()}>×</button>
        </div>
        <p className="lb-dialog__lead">
          {bot
            ? "새 프로젝트를 만들면 바로 라오나봇과 대화를 시작합니다. 무료로 시작합니다."
            : "새 프로젝트를 만들고 연결도 도구를 엽니다."}
        </p>
        <label htmlFor="lbNewTitle">프로젝트 이름</label>
        <input
          id="lbNewTitle"
          value={title}
          maxLength={100}
          placeholder={bot ? "예: 화분 자동 물주기" : "예: 온습도 표시기"}
          onChange={(e) => setTitle(e.target.value)}
        />
        {bot && (
          <>
            <p className="lb-dialog__note">
              대화 · 설계에 쓴 사용량이 {FREE_KRW.toLocaleString("ko-KR")}원을 넘으면 이용료 {DEPOSIT.toLocaleString("ko-KR")}원을
              결제하고 이어서 진행하거나, 결제 없이 직접 만들기로 넘어갈 수 있습니다. 펌웨어 개발 · 빌드는 주문할 때 부품과 함께 냅니다(기본 5만원).
            </p>
            {svc && !svc.enabled && svc.admin && (
              <p className="lb-dialog__note lb-way__note--admin">관리자 — 서비스를 멈춘 동안에도 시험용으로 시작할 수 있습니다.</p>
            )}
          </>
        )}
        {error && <p className="lb-error">{error}</p>}
        <button type="submit" className="lb-btn lb-btn--primary lb-btn--block" disabled={busy}>
          {busy ? "만드는 중…" : "시작하기"}
        </button>
      </form>
    </dialog>
  );
}
